<template>
  <div v-if="parentMsisdn" class="select-number bg-surface-primary p-16">
    <h2 class="heading-xs mb-16">Huvudabonnemangets nummer</h2>
    <p class="heading-xxs">{{ parentMsisdn }}</p>
  </div>
  <div
    class="select-number bg-surface-primary p-16 relative"
    role="region"
    :aria-labelledby="headingId"
  >
    <div
      v-if="isMainPlanBlocking"
      class="absolute inset-0 z-10 cursor-not-allowed bg-grey-300 opacity-70"
      aria-hidden="true"
    />
    <p v-if="isMainPlanBlocking" class="sr-only" role="status">
      Nummerval för familjeanslutning är låst. Välj eller verifiera numret för
      huvudabonnemanget för att kunna fortsätta.
    </p>
    <div :inert="isMainPlanBlocking || undefined">
      <h2 :id="headingId" class="heading-xs mb-16">{{ header }}</h2>
      <div class="max-w-[415px]">
        <InputRadioGroup>
          <InputRadioWithContent
            v-model="numberConfig.keepNumberOption"
            :value="KEEP_NUMBER_OPTION__NEW"
            label="Nytt nummer"
            :group-name="fieldName"
          >
            <template #hidden>
              <SelectionNewNumber />
            </template>
          </InputRadioWithContent>

          <InputRadioWithContent
            v-model="numberConfig.keepNumberOption"
            :value="KEEP_NUMBER_OPTION__KEEP"
            label="Behåll nummer, står på dig"
            :group-name="fieldName"
          >
            <template #hidden>
              <SelectionKeepNumber
                :cart-item-id="cartItemId"
                :bundle-id="bundleId"
                :parent-msisdn="parentMsisdn"
              />
            </template>
          </InputRadioWithContent>

          <InputRadioWithContent
            v-if="isFamilyItem"
            v-model="numberConfig.keepNumberOption"
            :value="KEEP_NUMBER_OPTION__KEEP_OTHER"
            label="Behåll nummer, står på en annan person"
            :group-name="fieldName"
          >
            <template #hidden>
              <SelectionKeepNumberTransferOwner
                :cart-item-id="cartItemId"
                :bundle-id="bundleId"
                :parent-msisdn="parentMsisdn"
              />
            </template>
          </InputRadioWithContent>
        </InputRadioGroup>
        <ErrorMessage v-slot="{ message }" :name="fieldName">
          <AppErrorText class="pt-16">
            {{ message }}
          </AppErrorText>
        </ErrorMessage>
      </div>
      <div class="mt-16 pt-16 border-t-2 border-border-secondary">
        <InputCheckbox
          v-model="showNumberPublicly"
          label="Jag samtycker till att mitt nummer delas med nummerupplysningsbolag"
        />
      </div>
      <div v-if="esimSupported">
        <InputCheckbox
          v-model="isEsim"
          label="Välj eSIM istället för fysiskt SIM-kort"
          label-extra="Med eSIM kan du istället för att sätta i ett simkort i din telefon få det digitalt förinstallerat. Om din telefon inte stödjer förinstallering får du istället en QR kod att skanna när abonnemanget skall aktiveras. Mindre mekande och mer miljövänligt helt enkelt!"
        />
      </div>
      <div v-if="esimRequired">
        <AppNotification type="info" class="max-w-[415px] mt-20">
          <template #header>
            <nuxt-icon
              filled
              name="alert-circle-blue"
              class="text-[2.4rem]"
              aria-hidden="true"
            />
            <h4 class="heading-xxs">Info om eSIM</h4>
          </template>
          <p class="mb-8">
            Den här mobilen stödjer bara eSIM. Du kommer få en QR-kod skickad
            till din e-post för att aktivera ditt eSIM.
          </p>

          <button
            type="button"
            class="flex align-middle gap-8 p-9 items-center body-link-md text-text-link underline halebop-focus hover:text-text-link-hover active:bg-surface-action-hover"
            @click="isDrawerOpen = true"
          >
            <span> Läs mer om eSIM </span>
          </button>
        </AppNotification>
        <AppDrawer
          v-model="isDrawerOpen"
          dialog-titel="Mer info om lurarnas skick"
        >
          <template #header>
            <div class="flex gap-8 flex-col">
              <h2 class="heading-xs">Vad är eSIM?</h2>
              <p>
                eSIM är ett digitalt simkort som installeras i din mobil. Det
                innebär att du inte behöver ditt fysiska SIM-kort längre.
              </p>
            </div>
          </template>
          <div class="flex flex-col gap-32">
            <div class="flex flex-col gap-16">
              <p class="heading-xxs">Såhär fungerar det:</p>
              <p>
                En QR-kod skickas till din e-post. När du startar mobilen första
                gången ansluter du till Wi-Fi och skannar sen QR-koden för att
                aktivera ditt eSIM
              </p>
            </div>
            <div class="flex flex-col gap-16">
              <h4 class="heading-xxs">Fördelar:</h4>
              <PdpCheckIconWithLabel label="Snabb och enkel aktivering" />
              <PdpCheckIconWithLabel
                label="Flera abonnemang/nummer i samma mobil"
              />
              <PdpCheckIconWithLabel
                label="Inget fysiskt simkort - slipp leta efter den lilla nålen när du byter mobil"
              />
            </div>
          </div>
        </AppDrawer>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  KEEP_NUMBER_OPTION__NEW,
  KEEP_NUMBER_OPTION__KEEP,
  KEEP_NUMBER_OPTION__KEEP_OTHER,
} from '~/models/checkout/checkout_store/NumberConfigCheckoutState.model';
import SelectionNewNumber from './SelectionNewNumber.vue';
import SelectionKeepNumber from './SelectionKeepNumber.vue';
import SelectionKeepNumberTransferOwner from './SelectionKeepNumberTransferOwner.vue';

interface Props {
  header?: string;
  cartItemId: number;
  bundleId: number;
  fieldName: string;
  parentMsisdn?: string;
  isFamilyItem: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  header: '',
  parentMsisdn: undefined,
});

const checkoutStore = useMyCheckoutStore();
const isDrawerOpen = ref(false);
const {
  esimRequired,
  esimSupported,
  isEsim,
  numberConfig,
  reset,
  showNumberPublicly,
} = useSelectNumberConfig({
  cartItemBundleId: props.bundleId,
  cartItemId: props.cartItemId,
  noOptionSelectedIntially: props.isFamilyItem,
});

const headingId = computed(
  () => `number-selection-heading-${props.bundleId}-${props.cartItemId}`,
);

const isMainPlanBlocking = computed(() => {
  if (!props.isFamilyItem) return false;

  const mainCartItems =
    checkoutStore.fetchedData.cart.cartItems?.filter(
      (item) =>
        item.BundleId != null &&
        !(item.BundlePlanItem?.IsFamilyPlan || item.IsFamilyPlan),
    ) ?? [];

  if (mainCartItems.length === 0) return false;

  return !mainCartItems.every((item) => {
    const id = item.BundlePlanItem?.Id ?? item.Id;
    const key = checkoutStore_createNumberSelectionMapKey(item.BundleId!, id);
    const config = checkoutStore.userState.numberConfigMap[key];
    if (!config) return true;
    return (
      config.keepNumberOption === KEEP_NUMBER_OPTION__NEW ||
      config.keepNumberCodeValidated
    );
  });
});

watch(
  () => numberConfig.value.keepNumberOption,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      reset();
    }
  },
);
</script>

<style scoped lang="scss">
.select-number {
  .success {
    box-shadow: inset 0.2rem 0rem 0 0.1rem $green-500;
  }
}
</style>
